import { Modal } from '@/components/admin/Modal';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { PasswordInput } from '@/components/ui/password-input';
import { ApiError, api } from '@/lib/api';
import { type ChangePasswordRequest, PasswordSchema } from '@innermate/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface FormState {
  current: string;
  next: string;
  confirm: string;
}

const EMPTY: FormState = { current: '', next: '', confirm: '' };

export function ChangePasswordDialog({ open, onClose }: Props) {
  if (!open) return null;
  return <ChangePasswordForm onClose={onClose} />;
}

function ChangePasswordForm({ onClose }: { onClose: () => void }) {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: ChangePasswordRequest) => api.changePassword(body),
    onSuccess: () => {
      setForm(EMPTY);
      setErrors({});
      setServerError(null);
      window.alert('비밀번호가 변경되었습니다.');
      onClose();
    },
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '비밀번호 변경에 실패했습니다.');
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);

    const next: Record<string, string> = {};
    if (!form.current) next.current = '현재 비밀번호를 입력해 주세요.';
    const parsed = PasswordSchema.safeParse(form.next);
    if (!parsed.success) {
      next.next = parsed.error.issues[0]?.message ?? '비밀번호 형식이 올바르지 않습니다.';
    }
    if (form.next !== form.confirm) {
      next.confirm = '새 비밀번호가 일치하지 않습니다.';
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});
    mutation.mutate({ current: form.current, next: form.next });
  };

  return (
    <Modal open onClose={onClose} title="비밀번호 변경" size="md">
      <form onSubmit={submit} className="space-y-4">
        <Field label="현재 비밀번호" htmlFor="cp-current" error={errors.current}>
          <PasswordInput
            id="cp-current"
            value={form.current}
            onChange={(e) => setForm((f) => ({ ...f, current: e.target.value }))}
            autoComplete="current-password"
            disabled={mutation.isPending}
          />
        </Field>

        <Field
          label="새 비밀번호"
          htmlFor="cp-next"
          hint="8자 이상, 영문과 숫자 모두 포함"
          error={errors.next}
        >
          <PasswordInput
            id="cp-next"
            value={form.next}
            onChange={(e) => setForm((f) => ({ ...f, next: e.target.value }))}
            autoComplete="new-password"
            disabled={mutation.isPending}
          />
        </Field>

        <Field label="새 비밀번호 확인" htmlFor="cp-confirm" error={errors.confirm}>
          <PasswordInput
            id="cp-confirm"
            value={form.confirm}
            onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))}
            autoComplete="new-password"
            disabled={mutation.isPending}
          />
        </Field>

        {serverError ? <p className="text-sm text-destructive">{serverError}</p> : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            취소
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? '변경 중…' : '변경'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
